
import {
  Component, Prop, Ref, Vue,
} from 'vue-property-decorator'
import ViewModel from '@/models/ViewModel'
import ReportBuilderForm from '@/pages/ReportBuilder/ReportBuilderForm.vue'
import { getModule } from 'vuex-module-decorators'
import ReportBuilder from '@/models/ReportBuilder'
import SystemtModule from '@/store/SystemModule'
import ReportViewer from '@/pages/ReportBuilder/ReportViewer.vue'
import ChartError from '@/models/interface/ChartError'
import ChartBuilder from '@/models/Charts/ChartBuilder'
import CustomIcon from '@/components/CustomIcon/CustomIcon.vue'
import moment from 'moment'
import WebMessage from '@/models/WebMessage'
import DatePickerDate from '@/models/DatePickerDate'
import FooterNav from '@/components/FooterNav/FooterNav.vue'
import Widget from '@/components/Widget/Widget.vue'

@Component({
  components: {
    ReportBuilderForm,
    ReportViewer,
    CustomIcon,
    FooterNav,
    Widget,
  },
})
export default class Builder extends ViewModel {
  @Ref() readonly settingsWidget!: Widget

  @Prop()
  public custom_filters!: any

  @Prop()
  public settings!: any

  public report: ReportBuilder = new ReportBuilder()

  public raw_data: any | null = null

  public selected_report_type: string = 'delivery'

  public show_filters: boolean = false

  public busy: boolean = false

  public search_for: any = ''

  public custom_to_options: any = []

  public to_options: any = []

  public report_types: any = []

  public loading: boolean = true

  public loaded_settings: any = []

  public fields: object[] = []

  public sortBy: string = 'date'

  public sortDesc: boolean = true

  public ready = false

  public ran_report = false

  public report_channel: any = null

  public view_as: string = 'table:table'

  public standalone: boolean = false

  public formReady: boolean = false

  public get active_view() {
    return this.view_options.find((view: any) => view.value === this.view_as)
  }

  public get view_mode() {
    let ret = this.view_as.split(':')

    return { type: ret[0], subtype: ret[1] ?? '' }
  }

  public get SETTINGS() {
    return {
      inputs: this.settings && this.settings.inputs ? this.settings.inputs : {},
      filters: this.settings && this.settings.filters ? this.settings.filters : {},
    }
  }

  public modal: any = {
    delete: false,
    send: false,
    filter: false,
    save: false,
  }

  private registerEvent() {
    this.report_channel = Vue.prototype.$echo
      .private(`user.${this.user!.id}`)
      .listen('AsyncReportReady', (e: any) => {
        const instance_id = getModule(SystemtModule)._uuid

        if (instance_id && instance_id !== e.instance_id) return

        if (!e.report_hash) {
          this.loading = false

          return
        }

        ReportBuilder.getReport(e.report_hash)
          .then(response => {
            if (response.data.result) this.raw_data = response.data
            this.loading = false
            this.ran_report = true
          })
          .catch(error => {
            this.loading = false
          })
      })
  }

  public onSettingsChange(data: any) {
    this.loaded_settings = data

    if (this.settings && this.settings.run_on_mounted) {
      setTimeout(() => {
        this.run(() => {
          this.formReady = true
        })
      }, 300)
    } else {
      this.formReady = true
    }
  }

  public isViewCompatible(view: any) {
    if (!this.raw_data || !this.raw_data.result || this.raw_data.result.length === 0) {
      return { compatible: false, reason: 'No data' }
    }

    if (view.value === 'table:table') {
      return {
        compatible: true,
      }
    }

    const type = view.value.split(':')[1]

    try {
      ChartBuilder.isCompatible(type, this.raw_data)
    } catch (e) {
      if (e instanceof ChartError) {
        return {
          compatible: false,
          reason: e.message,
        }
      }
      return {
        compatible: false,
        reason: 'Unknown error',
      }
    }

    return {
      compatible: true,
    }
  }

  public view_options: any[] = [
    { name: 'Table', value: 'table:table', icon: 'table' },
    { name: 'Bar Chart', value: 'chart:bar', icon: 'chart-bar' },
    { name: 'Line Chart', value: 'chart:line', icon: 'chart-line' },
    // { name: 'Area Chart', value: 'chart:area', icon: 'chart-area' },
    { name: 'Pie Chart', value: 'chart:pie', icon: 'chart-pie' },
    { name: 'Radial Chart', value: 'chart:radialBar', customIcon: 'radial' },
    {
      name: 'Gauge Chart',
      value: 'chart:gauge',
      customIcon: 'gauge',
    },
    { name: 'Treemap Chart', value: 'chart:treemap', customIcon: 'treemap' },
    { name: 'Heatmap Chart', value: 'chart:heatmap', icon: 'fire-alt' },
    { name: 'Funnel Chart', value: 'chart:funnel', icon: 'filter' },
    { name: 'Radar Chart', value: 'chart:radar', customIcon: 'radar' },
  ]

  public beforeDestroy() {
    this.unregisterEvent()
  }

  private unregisterEvent() {
    Vue.prototype.$echo.private(`user.${this.user!.id}`).stopListening('AsyncReportReady')
  }

  public mounted() {
    this.registerEvent()
    const { query } = this.$route
    let clone = false
    let target = this.$route.params.report_id || null

    if (this.custom_filters) {
      let obj = Object.assign(this.report.filters, this.custom_filters)
      Vue.set(this.report, 'filters', obj)
    }

    if (this.settings && this.settings.hasOwnProperty('filter_dates')) {
      this.report.period_picker = Object.assign(
        this.report.period_picker,
        this.settings.filter_dates,
      )
    }

    if (query.from && typeof query.from === 'string') {
      clone = true
      target = query.from
    }

    if (target) {
      ReportBuilder.find(target).then(o => {
        if (o instanceof ReportBuilder) {
          this.report = ReportBuilder.toObject(o)
          if (clone) {
            this.report.id = null
            this.report.name += ' (Copy)'
          }
        }

        this.loading = false

        this.busy = false
        if (query.run && query.run == '1') {
          setTimeout(() => {
            this.run()
          }, 250)
        } else {
          this.ready = true
        }
      })

      return
    }

    this.standalone = true

    this.loading = false
    this.busy = false
    this.ready = true
  }

  public download() {
    this.loading = true
    WebMessage.success('Your report is being generated. Please wait for the download to start.')
    this.report.download().then(() => {
      this.loading = false
    })
  }

  public run(onReady: any = null) {
    this.checkDateFilters()

    if (this.selected_settings && this.selected_settings.export_only) {
      this.download()
      return
    }

    this.loading = true
    this.settingsWidget.collapseWidget(true)
    this.report
      .runReport()
      .then(response => {
        if (response.data.result) {
          this.raw_data = response.data

          this.loading = false
          this.ran_report = true

          setTimeout(() => {
            this.ready = true
            if (typeof onReady === 'function') {
              onReady(true)
            }
          }, 100)
        } else {
          this.ready = true
          if (typeof onReady === 'function') {
            onReady(true)
          }
        }
      })
      .catch(error => {
        this.loading = false
        this.settingsWidget.collapseWidget(false)
        this.ready = true
      })
  }

  public get active_date_settings() {
    if (this.report.period_filter === 'none') {
      return this.selected_settings.filter_dates[0]
    }

    return this.selected_settings.filter_dates.find(
      (d: any) => d.value === this.report.period_filter,
    )
  }

  public checkDateFilters() {
    if (!this.active_date_settings) {
      return
    }
    if (this.active_date_settings.type == 'year') {
      this.report.period_picker.start = moment(this.report.period_picker.start)
        .startOf('year')
        .format('YYYY-MM-DD')
    }
    if (!this.active_date_settings.range) {
      this.report.period_picker.end = moment(this.report.period_picker.start)
        .endOf(this.active_date_settings.type == 'year' ? 'year' : 'month')
        .format('YYYY-MM-DD')
      this.report.period_picker.shortcut = null
    }
  }

  public get selected_settings() {
    if (this.loaded_settings.length) {
      let settings = this.loaded_settings.find(
        (settings: any) => settings.value === this.report.type,
      )
      if (settings) {
        return settings
      }
    }

    return {
      filter_dates: [],
      dimension_options: [],
      dimensions: [],
      metric_options: [],
      metrics: [],
    }
  }
}
